'use client';

import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  buildDomainTreeSignalCounts,
  type DomainTree,
} from '@terra/ui-design-system';
import { terraProxyApiClient } from '../../clients/testerClient';
import type { LifecycleStateFilterDto } from '@terra/shared-dto-types';

type UseDomainTreePanelCountsParams = {
  appTargetId: string | undefined;
  shouldApplyAppLevel: boolean | undefined;
  isExecutionsTab: boolean | undefined;
  domainTree: DomainTree | null;
  isDomainTreeFetching: boolean;
  refetchDomainTree: () => void;
  lifecycleState?: LifecycleStateFilterDto;
};

export function useDomainTreePanelCounts({
  appTargetId,
  shouldApplyAppLevel,
  isExecutionsTab,
  domainTree,
  isDomainTreeFetching,
  refetchDomainTree,
  lifecycleState,
}: UseDomainTreePanelCountsParams) {
  const shouldFetchSignalEndpointCounts =
    !!appTargetId && !!shouldApplyAppLevel;

  const shouldFetchTraceEndpointCounts = !!appTargetId && !!isExecutionsTab;

  const {
    data: signalEndpointCountsData,
    refetch: refetchSignalEndpointCounts,
    isFetching: isFetchingSignalEndpointCounts,
  } = useQuery({
    queryKey: [
      'signals-count-by-endpoint',
      appTargetId,
      shouldApplyAppLevel,
      lifecycleState,
    ],
    queryFn: async () => {
      if (!appTargetId) {
        return null;
      }

      return terraProxyApiClient.getSignalsCountByEndpoint(
        appTargetId,
        lifecycleState ? { lifecycleState } : undefined,
        undefined,
        shouldApplyAppLevel,
      );
    },
    enabled: shouldFetchSignalEndpointCounts,
    staleTime: 30000,
  });

  const {
    data: traceEndpointCountsData,
    refetch: refetchTraceEndpointCounts,
    isFetching: isFetchingTraceEndpointCounts,
  } = useQuery({
    queryKey: ['trace-counts-by-endpoint', appTargetId, lifecycleState],
    queryFn: async () => {
      if (!appTargetId) {
        return null;
      }

      const response = await terraProxyApiClient.getTraceCountsByEndpoint({
        applicationId: appTargetId,
        agentName: 'neo',
        lifecycleState,
      });

      if (response.status === 200) {
        return response.body;
      }

      return null;
    },
    enabled: shouldFetchTraceEndpointCounts,
    staleTime: 30000,
  });

  const nodeIdToCountMap = useMemo(() => {
    if (isExecutionsTab) {
      return buildDomainTreeSignalCounts({
        domainTree,
        endpointCounts: (traceEndpointCountsData?.endpointCounts ?? []).map(
          ({ endpointId, traceCount }) => ({
            endpointId,
            signalCount: traceCount,
          }),
        ),
      });
    }

    if (!shouldApplyAppLevel) {
      return undefined;
    }

    return buildDomainTreeSignalCounts({
      domainTree,
      endpointCounts: signalEndpointCountsData?.endpointCounts ?? [],
    });
  }, [
    shouldApplyAppLevel,
    isExecutionsTab,
    domainTree,
    signalEndpointCountsData?.endpointCounts,
    traceEndpointCountsData?.endpointCounts,
  ]);

  const isRefreshing =
    isDomainTreeFetching ||
    isFetchingSignalEndpointCounts ||
    isFetchingTraceEndpointCounts;

  const handleRefresh = useCallback(() => {
    void refetchDomainTree();
    if (shouldApplyAppLevel) {
      void refetchSignalEndpointCounts();
    }

    if (isExecutionsTab) {
      void refetchTraceEndpointCounts();
    }
  }, [
    refetchDomainTree,
    shouldApplyAppLevel,
    refetchSignalEndpointCounts,
    isExecutionsTab,
    refetchTraceEndpointCounts,
  ]);

  return {
    nodeIdToCountMap,
    isRefreshing,
    handleRefresh,
  };
}
